import React, { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';

import {
  actions,
  createTransactionHistoryDefaultFilter,
  DateService,
  prepareRequest,
  SiteConfigManager,
  TransactionHistoryProvider,
  useTransactionHistory,
  useTransactionHistoryDispatch,
} from '@atlas/core';
import { ChipsItem, EmptyStateView, Label, Loader } from '@atlas/ui';

import TransactionHistoryTableItem from 'components/ClientProfile/Payment/Transaction/TransactionHistoryTableItem';
import SeoPageText from 'components/SeoPage/SeoPageText';
import { getSearchParam } from 'src/helpers/getSearchParam';

import TransactionHistoryFilter, { getPaymentOperations } from './TransactionHitoryFilter';

const DEFAULT_OPTION = 'all';

const ConnectedTransactionHistory = () => {
  const options = {
    filter: {},
  };

  const searchParam = getSearchParam(window.location.search, 'operation');
  const paymentOperations = getPaymentOperations({
    name: DEFAULT_OPTION,
    value: DEFAULT_OPTION,
  });
  if (searchParam) {
    const filter = paymentOperations.find(el => el.name === searchParam);
    if (filter) {
      options.filter.operation = filter.value;
    }
  }
  return (
    <TransactionHistoryProvider options={options}>
      <TransactionHistoryView paymentOperations={paymentOperations} />
    </TransactionHistoryProvider>
  );
};

const TransactionHistoryTableWrapper = ({ loading, paymentMethods, transactions }) => {
  if (loading) {
    return <Loader className="relative" />;
  }

  if (transactions && transactions.length === 0) {
    return (
      <EmptyStateView
        className="empty-center"
        description="transaction_history_empty_message"
        title="transaction_history_empty_title"
      />
    );
  }

  const dateFormat = SiteConfigManager.getConfig('config.config.date.dateFormat');
  const transactionDatesFormat = dateFormat === 'MM.DD' ? 'MMM DD YYYY' : 'DD MMM YYYY';
  const transactionDates = transactions.reduce(
    (acc, transaction, index) => {
      const formattedDate = DateService.getFormattedDate(
        transaction.createDate,
        transactionDatesFormat,
      );

      if (acc.prevDataIndex === null) {
        return {
          data: { 0: formattedDate },
          prevDataIndex: 0,
        };
      }

      if (acc.data[acc.prevDataIndex] !== formattedDate) {
        return {
          data: {
            ...acc.data,
            [index]: formattedDate,
          },
          prevDataIndex: index,
        };
      }

      return acc;
    },
    {
      data: {},
      prevDataIndex: null,
    },
  );

  return transactions.map((transaction, index) => (
    <>
      {!!transactionDates.data[index] && (
        <div className="transaction-history-list-date">{transactionDates.data[index]}</div>
      )}
      <TransactionHistoryTableItem
        key={transaction.id}
        paymentMethods={paymentMethods}
        transaction={transaction}
      />
    </>
  ));
};

TransactionHistoryTableWrapper.propTypes = {
  loading: PropTypes.bool,
  paymentMethods: PropTypes.arrayOf(PropTypes.object),
  transactions: PropTypes.arrayOf(PropTypes.object),
};

const TransactionHistoryView = ({ paymentOperations }) => {
  const { currentSlice, filter, hasNext, loading, moreLoading, transactions } =
    useTransactionHistory();
  const [filterData, setFilterData] = useState(filter);
  const [paymentMethods, setPaymentMethods] = useState([]);

  const dispatch = useTransactionHistoryDispatch();

  useEffect(() => {
    const request = prepareRequest({
      method: 'GET',
      url: 'payment/v2/methods',
    });

    request().then(({ data }) => {
      setPaymentMethods(data);
    });
  }, []);

  const isDisabledResetBtn = useMemo(() => {
    const { createDate } = createTransactionHistoryDefaultFilter();
    return (
      createDate.from === filterData.createDate.from &&
      createDate.to === filterData.createDate.to &&
      (!filterData.customOperation || filterData.customOperation === DEFAULT_OPTION) &&
      (!filterData.pmId || filterData.pmId === DEFAULT_OPTION) &&
      (!filterData.status || filterData.status === DEFAULT_OPTION)
    );
  }, [filterData]);

  const isDisabledApplyBtn = useMemo(() => {
    return (
      filter.createDate.to === filterData.createDate.to &&
      filter.createDate.from === filterData.createDate.from &&
      ((!filter.pmId && filterData.pmId === DEFAULT_OPTION) || filter.pmId === filterData.pmId) &&
      ((!filter.status && filterData.status === DEFAULT_OPTION) ||
        filter.status === filterData.status) &&
      ((!filter.customOperation && filterData.customOperation === DEFAULT_OPTION) ||
        filter.customOperation === filterData.customOperation)
    );
  }, [filter, filterData]);

  const getFilterRightDate = React.useCallback((filterData, { withSetFilter = false } = {}) => {
    const minFromDate = dayjs()
      .subtract(+(DateService.TIME_RANGES.MONTH * 2.88).toFixed(0), 'ms')
      .startOf('day')
      .valueOf();

    if (minFromDate > filterData.createDate.from) {
      const newData = {
        ...filterData,
        createDate: {
          ...filterData.createDate,
          from: minFromDate,
        },
      };

      if (minFromDate > newData.createDate.to) {
        newData.createDate.to = dayjs().endOf('day').valueOf();
      }

      withSetFilter && setFilterData(newData);
      return newData;
    }

    return filterData;
  }, []);

  const loadMoreHandler = React.useCallback(() => {
    if (!loading && !moreLoading) {
      dispatch({
        payload: {
          currentSlice: currentSlice + 1,
          filter: getFilterRightDate(filter),
        },
        type: actions.STATE_UPDATE,
      });
    }
  }, [currentSlice, dispatch, filter, getFilterRightDate, loading, moreLoading]);

  const applyFilterHandler = React.useCallback(() => {
    if (!loading && !moreLoading) {
      const newData = getFilterRightDate(filterData, { withSetFilter: true });

      dispatch({
        payload: {
          ...Object.keys(newData).reduce((acc, key) => {
            return { ...acc, [key]: newData[key] === DEFAULT_OPTION ? null : newData[key] };
          }, {}),
        },
        type: actions.FILTER_UPDATE,
      });
    }
  }, [dispatch, filterData, getFilterRightDate, loading, moreLoading]);

  const resetFilterHandler = React.useCallback(() => {
    if (!loading && !moreLoading) {
      const newFilterData = {
        ...createTransactionHistoryDefaultFilter(),
        operation: filter.operation,
      };

      setFilterData(newFilterData);

      dispatch({
        payload: {
          currentSlice: 0,
          filter: newFilterData,
          hasNext: false,
        },
        type: actions.STATE_UPDATE,
      });
    }
  }, [dispatch, filter.operation, loading, moreLoading]);

  const typesChangeHandler = React.useCallback(
    operationValue => {
      const operation = operationValue === DEFAULT_OPTION ? null : operationValue;
      const newData = getFilterRightDate({ ...filter, operation });
      setFilterData(newData);

      dispatch({
        payload: {
          currentSlice: 0,
          filter: newData,
          hasNext: false,
        },
        type: actions.STATE_UPDATE,
      });
    },
    [dispatch, filter, getFilterRightDate],
  );

  const dateChangeHandler = React.useCallback(
    range => {
      setFilterData({
        ...filterData,
        createDate: {
          from: dayjs(range.from).startOf('day').valueOf(),
          to: dayjs(range.to).endOf('day').valueOf(),
        },
      });
    },
    [filterData],
  );

  const customOperationChangeHandler = React.useCallback(
    customOperation => {
      setFilterData({
        ...filterData,
        customOperation,
      });
    },
    [filterData],
  );

  const paymentMethodChangeHandler = React.useCallback(
    data => {
      setFilterData({
        ...filterData,
        pmId: data,
      });
    },
    [filterData],
  );

  const statusChangeHandler = React.useCallback(
    data => {
      setFilterData({
        ...filterData,
        status: data,
      });
    },
    [filterData],
  );

  return (
    <div className="profile-transaction__block">
      <TransactionHistoryFilter
        DEFAULT_OPTION={DEFAULT_OPTION}
        applyHandler={applyFilterHandler}
        customOperationChangeHandler={customOperationChangeHandler}
        filter={filterData}
        isDisabledApplyBtn={isDisabledApplyBtn}
        isDisabledResetBtn={isDisabledResetBtn}
        onDateChange={dateChangeHandler}
        paymentMethodChangeHandler={paymentMethodChangeHandler}
        paymentMethods={paymentMethods}
        paymentOperations={paymentOperations}
        resetHandler={resetFilterHandler}
        statusChangeHandler={statusChangeHandler}
        typesChangeHandler={typesChangeHandler}
      />

      <div className="transaction-history-list profile-payments-wrapper">
        <TransactionHistoryTableWrapper
          loading={loading}
          paymentMethods={paymentMethods}
          transactions={transactions}
        />

        {hasNext && (
          <div className="casino-load-more-block">
            <ChipsItem
              className="casino-load-more-block__button"
              label={<Label message="payment_history_load_more" />}
              onClick={loadMoreHandler}
            />
          </div>
        )}
      </div>

      <SeoPageText />
    </div>
  );
};

export default ConnectedTransactionHistory;
